import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

const TransportPersons = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [transportPersons, setTransportPersons] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [driverToArchive, setDriverToArchive] = useState(null);
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedDriverName, setArchivedDriverName] = useState('');
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [driverToRestore, setDriverToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredDriverName, setRestoredDriverName] = useState('');
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [transportPersonToDelete, setTransportPersonToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedTransportPersonName, setDeletedTransportPersonName] = useState('');
  const [viewingDriver, setViewingDriver] = useState(null);
  const [viewingDriverProfileUrl, setViewingDriverProfileUrl] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    car_number: '',
    phone: '',
    license_number: '',
    address: '',
    country: '',
    city: '',
    postal_code: '',
    national_id: '',
    is_active: true,
    profile_picture: null
  });
  const [editProfilePicturePreview, setEditProfilePicturePreview] = useState(null);
  const [uploadingEditPicture, setUploadingEditPicture] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      const parsedData = JSON.parse(storedSchoolData);
      setSchoolData(parsedData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchTransportPersons();
    }
  }, [schoolData?.id, viewMode, selectedBranchId]);

  // Generate signed URL when viewing driver changes
  useEffect(() => {
    const generateProfileUrl = async () => {
      if (viewingDriver?.profile_picture_url) {
        try {
          console.log('useEffect: Generating signed URL for driver profile picture:', viewingDriver.profile_picture_url);
          
          // Always generate a new signed URL when driver changes
          const signedUrl = await getValidFileUrl(viewingDriver.profile_picture_url);
          console.log('useEffect: Generated signed URL:', signedUrl);
          
          if (signedUrl) {
            setViewingDriverProfileUrl(signedUrl);
          } else {
            // Use original URL if signed URL generation fails
            setViewingDriverProfileUrl(viewingDriver.profile_picture_url);
          }
        } catch (err) {
          console.error('useEffect: Error generating profile picture URL:', err);
          // Use original URL as fallback
          setViewingDriverProfileUrl(viewingDriver.profile_picture_url);
        }
      } else {
        setViewingDriverProfileUrl(null);
      }
    };

    if (viewingDriver) {
      generateProfileUrl();
    }
  }, [viewingDriver?.id, viewingDriver?.profile_picture_url]);

  const fetchTransportPersons = async () => {
    if (!schoolData?.id) {
      return;
    }

    try {
      setLoadingDrivers(true);
      let query = supabase
        .from('transport_persons')
        .select('*')
        .eq('school_id', schoolData.id);
      
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        query = query.eq('is_active', true);
      } else {
        query = query.eq('is_active', false);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transport persons:', error);
        return;
      }

      setTransportPersons(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const filteredTransportPersons = transportPersons;

  const handleArchiveTransportPerson = (person) => {
    setDriverToArchive(person);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveDriver = async () => {
    if (!driverToArchive) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('transport_persons')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', driverToArchive.id);
      
      if (error) {
        console.error('Error archiving transport person:', error);
        alert('Failed to archive driver. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setDriverToArchive(null);
        return;
      }

      // Store archived driver name for success popup
      const archivedName = driverToArchive.name || `${driverToArchive.first_name} ${driverToArchive.last_name}`;
      
      // Refresh the list
      await fetchTransportPersons();
      setShowArchiveConfirmPopup(false);
      setDriverToArchive(null);
      
      // Show success popup
      setArchivedDriverName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving transport person:', err);
      alert('Failed to archive driver. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveDriver = () => {
    setShowArchiveConfirmPopup(false);
    setDriverToArchive(null);
  };

  const handleRestoreTransportPerson = (person) => {
    setDriverToRestore(person);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreDriver = async () => {
    if (!driverToRestore) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('transport_persons')
        .update({ 
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', driverToRestore.id);
      
      if (error) {
        console.error('Error restoring transport person:', error);
        alert('Failed to restore driver. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setDriverToRestore(null);
        return;
      }

      // Store restored driver name for success popup
      const restoredName = driverToRestore.name || `${driverToRestore.first_name} ${driverToRestore.last_name}`;
      
      // Refresh the list
      await fetchTransportPersons();
      setShowRestoreConfirmPopup(false);
      setDriverToRestore(null);
      
      // Show success popup
      setRestoredDriverName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring transport person:', err);
      alert('Failed to restore driver. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreDriver = () => {
    setShowRestoreConfirmPopup(false);
    setDriverToRestore(null);
  };

  const handleToggleStatus = async (personId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('transport_persons')
        .update({ 
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', personId);

      if (error) {
        console.error('Error updating transport person status:', error);
        alert('Failed to update status. Please try again.');
        return;
      }

      // Refresh the list
      fetchTransportPersons();
      alert(`Driver ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleDeleteTransportPerson = (personId, personName) => {
    setTransportPersonToDelete({
      id: personId,
      name: personName
    });
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteTransportPerson = async () => {
    if (!transportPersonToDelete) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('transport_persons')
        .delete()
        .eq('id', transportPersonToDelete.id);

      if (error) {
        console.error('Error deleting transport person:', error);
        alert('Failed to delete driver. Please try again.');
        setIsSubmitting(false);
        setShowDeleteConfirmPopup(false);
        setTransportPersonToDelete(null);
        return;
      }

      // Store deleted transport person name for success popup
      const deletedName = transportPersonToDelete.name;

      // Refresh the list
      await fetchTransportPersons();
      
      // Close confirmation popup and show success popup
      setShowDeleteConfirmPopup(false);
      setTransportPersonToDelete(null);
      setDeletedTransportPersonName(deletedName);
      setShowDeleteSuccessPopup(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete driver. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteTransportPerson = () => {
    setShowDeleteConfirmPopup(false);
    setTransportPersonToDelete(null);
  };

  const getValidFileUrl = async (url) => {
    if (!url) return null;
    
    try {
      console.log('getValidFileUrl called with URL:', url);
      
      // Clean the URL - remove any query parameters or fragments
      let cleanUrl = url.split('?')[0].split('#')[0];
      
      // If it's already a full URL, check if it's a Supabase storage URL
      if (cleanUrl.includes('supabase.co/storage/v1/object')) {
        // Handle both public and signed URL formats
        // Public URL format: /storage/v1/object/public/[bucket]/[path]
        // Signed URL format: /storage/v1/object/sign/[bucket]/[path]
        // We need to extract bucket and path from either format
        
        let bucket = null;
        let filePath = null;
        
        // Try to match public URL format first
        const publicMatch = cleanUrl.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)/);
        if (publicMatch) {
          bucket = publicMatch[1];
          filePath = publicMatch[2];
          console.log('Matched public URL format. Bucket:', bucket, 'Path:', filePath);
        } else {
          // Try to match signed URL format
          const signedMatch = cleanUrl.match(/\/storage\/v1\/object\/sign\/([^/]+)\/(.+?)(\?|$)/);
          if (signedMatch) {
            bucket = signedMatch[1];
            filePath = signedMatch[2];
            console.log('Matched signed URL format. Bucket:', bucket, 'Path:', filePath);
          } else {
            // Try generic format
            const genericMatch = cleanUrl.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/);
            if (genericMatch) {
              bucket = genericMatch[1];
              filePath = genericMatch[2];
              console.log('Matched generic URL format. Bucket:', bucket, 'Path:', filePath);
            }
          }
        }
        
        if (bucket && filePath) {
          // Decode the file path if it's URL encoded
          try {
            filePath = decodeURIComponent(filePath);
          } catch (e) {
            console.log('Could not decode file path, using as is');
          }
          
          console.log('Generating signed URL for bucket:', bucket, 'path:', filePath);
          
          // Generate signed URL for private bucket
          const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(filePath, 3600);
          
          if (error) {
            console.error('Error creating signed URL:', error);
            // Try to use public URL as fallback
            const { data: publicData } = supabase.storage
              .from(bucket)
              .getPublicUrl(filePath);
            console.log('Using public URL as fallback:', publicData?.publicUrl);
            return publicData?.publicUrl || url;
          }
          
          console.log('Generated signed URL:', data?.signedUrl);
          return data?.signedUrl || url;
        }
      }
      
      // If it's a relative path, assume it's in transport-uploads bucket
      if (cleanUrl.startsWith('transport-profiles/') || cleanUrl.includes('transport-profiles/')) {
        // Extract just the path part
        let filePath = cleanUrl;
        if (cleanUrl.includes('transport-profiles/')) {
          filePath = cleanUrl.substring(cleanUrl.indexOf('transport-profiles/'));
        }
        
        console.log('Using relative path. Generating signed URL for transport-uploads, path:', filePath);
        
        const { data, error } = await supabase.storage
          .from('transport-uploads')
          .createSignedUrl(filePath, 3600);
        
        if (error) {
          console.error('Error creating signed URL for transport-uploads:', error);
          // Try public URL as fallback
          const { data: publicData } = supabase.storage
            .from('transport-uploads')
            .getPublicUrl(filePath);
          return publicData?.publicUrl || url;
        }
        
        console.log('Generated signed URL for transport-uploads:', data?.signedUrl);
        return data?.signedUrl || url;
      }
      
      // If it's a public URL that doesn't match Supabase format, return as is
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        console.log('Returning URL as is (not Supabase storage URL)');
        return cleanUrl;
      }
      
      console.log('Returning original URL as fallback');
      return url;
    } catch (err) {
      console.error('Error in getValidFileUrl:', err);
      return url;
    }
  };

  const handleViewDriver = async (driver) => {
    console.log('handleViewDriver called with driver:', driver);
    console.log('Driver profile_picture_url:', driver.profile_picture_url);
    
    setViewingDriver(driver);
    
    // Reset profile URL first
    setViewingDriverProfileUrl(null);
    
    // Generate signed URL for profile picture if exists
    if (driver.profile_picture_url) {
      try {
        console.log('Generating signed URL for driver profile picture:', driver.profile_picture_url);
        
        // Check if URL is already a signed URL (contains token parameter)
        if (driver.profile_picture_url.includes('token=')) {
          console.log('URL already contains token, using as is');
          setViewingDriverProfileUrl(driver.profile_picture_url);
        } else {
          // Generate new signed URL
          const signedUrl = await getValidFileUrl(driver.profile_picture_url);
          console.log('Generated signed URL:', signedUrl);
          
          if (signedUrl) {
            setViewingDriverProfileUrl(signedUrl);
          } else {
            // Use original URL if signed URL generation fails
            console.log('Using original URL as fallback');
            setViewingDriverProfileUrl(driver.profile_picture_url);
          }
        }
      } catch (err) {
        console.error('Error generating profile picture URL for view:', err);
        // Set the original URL as fallback
        setViewingDriverProfileUrl(driver.profile_picture_url);
      }
    } else {
      console.log('Driver has no profile picture URL');
      setViewingDriverProfileUrl(null);
    }
  };

  const closeViewPopup = () => {
    setViewingDriver(null);
    setViewingDriverProfileUrl(null);
  };

  const handleEditDriver = async (driver) => {
    setEditingDriver(driver);
    setEditForm({
      name: driver.name || '',
      first_name: driver.first_name || '',
      middle_name: driver.middle_name || '',
      last_name: driver.last_name || '',
      email: driver.email || '',
      car_number: driver.car_number || '',
      phone: driver.phone || '',
      license_number: driver.license_number || '',
      address: driver.address || '',
      country: driver.country || '',
      city: driver.city || '',
      postal_code: driver.postal_code || '',
      national_id: driver.national_id || '',
      is_active: driver.is_active !== undefined ? driver.is_active : true,
      profile_picture: null
    });
    setEditProfilePicturePreview(null);
    
    // Generate signed URL for existing profile picture if it exists
    if (driver.profile_picture_url) {
      try {
        const signedUrl = await getValidFileUrl(driver.profile_picture_url);
        setEditProfilePicturePreview(signedUrl || driver.profile_picture_url);
      } catch (err) {
        console.error('Error loading profile picture for edit:', err);
        setEditProfilePicturePreview(driver.profile_picture_url);
      }
    }
  };

  const handleEditProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setEditForm(prev => ({
      ...prev,
      profile_picture: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeEditProfilePicture = () => {
    setEditForm(prev => ({
      ...prev,
      profile_picture: null
    }));
    setEditProfilePicturePreview(null);
  };

  const closeEditPopup = () => {
    setEditingDriver(null);
    setEditForm({
      name: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      car_number: '',
      phone: '',
      license_number: '',
      address: '',
      country: '',
      city: '',
      postal_code: '',
      national_id: '',
      is_active: true,
      profile_picture: null
    });
    setEditProfilePicturePreview(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingDriver) return;
    
    try {
      setIsSubmitting(true);
      
      // Upload new profile picture if provided
      let profilePictureUrl = editingDriver.profile_picture_url; // Keep existing URL by default
      
      if (editForm.profile_picture) {
        try {
          setUploadingEditPicture(true);
          console.log('Uploading new profile picture for driver:', editingDriver.id);
          
          const fileExt = editForm.profile_picture.name.split('.').pop();
          const fileName = `transport_${Date.now()}.${fileExt}`;
          const filePath = `transport-profiles/${schoolData.id}/${fileName}`;
          
          console.log('Uploading to path:', filePath);
          
          const { error: uploadError } = await supabase.storage
            .from('transport-uploads')
            .upload(filePath, editForm.profile_picture, {
              cacheControl: '3600',
              upsert: false
            });
          
          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            alert(`Failed to upload profile picture: ${uploadError.message}. Driver will be updated without changing the profile picture.`);
          } else {
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('transport-uploads')
              .getPublicUrl(filePath);
            
            profilePictureUrl = urlData.publicUrl;
            console.log('New profile picture URL generated:', profilePictureUrl);
          }
        } catch (uploadErr) {
          console.error('Exception during profile picture upload:', uploadErr);
          alert(`Error uploading profile picture: ${uploadErr.message}. Driver will be updated without changing the profile picture.`);
        } finally {
          setUploadingEditPicture(false);
        }
      }
      
      // Combine first, middle, and last name
      const fullName = [editForm.first_name, editForm.middle_name, editForm.last_name]
        .filter(name => name && name.trim() !== '')
        .join(' ');
      
      const updateData = {
        name: fullName || editForm.name,
        first_name: editForm.first_name.trim(),
        middle_name: editForm.middle_name?.trim() || null,
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        car_number: editForm.car_number.trim(),
        phone: editForm.phone.trim(),
        license_number: editForm.license_number?.trim() || null,
        address: editForm.address?.trim() || null,
        country: editForm.country.trim(),
        city: editForm.city.trim(),
        postal_code: editForm.postal_code.trim(),
        national_id: editForm.national_id?.trim() || null,
        is_active: editForm.is_active,
        updated_at: new Date().toISOString()
      };
      
      // Only update profile_picture_url if a new picture was uploaded
      if (editForm.profile_picture && profilePictureUrl) {
        updateData.profile_picture_url = profilePictureUrl;
      }
      
      console.log('Updating driver with data:', updateData);
      
      const { error } = await supabase
        .from('transport_persons')
        .update(updateData)
        .eq('id', editingDriver.id);
      
      if (error) {
        console.error('Error updating driver:', error);
        alert('Failed to update driver. Please try again.');
        return;
      }
      
      // Refresh the list
      await fetchTransportPersons();
      closeEditPopup();
      alert('Driver updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update driver. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadingEditPicture(false);
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Drivers Management</h1>
                <p>{viewMode === 'active' ? 'View and manage all active drivers in your school' : 'View and manage archived drivers'}</p>
              </div>
              <BranchSelector
                schoolId={schoolData?.id}
                selectedBranchId={selectedBranchId}
                onBranchChange={handleBranchChange}
              />
            </div>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{transportPersons.length}</span>
              <span className="stat-label">Total Drivers</span>
                </div>
            <div className="stat-item">
              <span className="stat-number">
                {transportPersons.filter(p => p.is_active).length}
              </span>
              <span className="stat-label">Active Drivers</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {transportPersons.filter(p => !p.is_active).length}
              </span>
              <span className="stat-label">Inactive Drivers</span>
                </div>
                </div>

          <div className="students-actions">
            {/* Add Driver Button */}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/school/add-transport-person')}
                className="submit-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>➕</span>
                <span>Add Driver</span>
              </button>
            </div>
            {/* View Mode Toggle */}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setViewMode('active')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'active' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                👥 Active Drivers
              </button>
              <button
                onClick={() => setViewMode('archived')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'archived' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📦 Archived Drivers
              </button>
            </div>
          </div>

          {loadingDrivers ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading drivers...</p>
            </div>
          ) : filteredTransportPersons.length === 0 ? (
                  <div className="empty-state">
              <div className="empty-icon">🚌</div>
              <h3>No {viewMode === 'active' ? 'Active' : 'Archived'} Drivers Found</h3>
              <p>
                {viewMode === 'active' 
                  ? "You haven't added any active drivers yet. Start by adding your first driver!"
                  : "You don't have any archived drivers yet."}
              </p>
              {viewMode === 'active' && (
                      <button
                  className="add-first-teacher-btn"
                        onClick={() => navigate('/school/add-transport-person')}
                      >
                  ➕ Add Your First Driver
                      </button>
                    )}
                </div>
              ) : (
            <div className="students-table-container">
              <table className="students-table" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                    <th style={{ width: '20%' }}>Driver Name</th>
                    <th style={{ width: '20%' }}>Email</th>
                    <th style={{ width: '15%' }}>Car Number</th>
                    <th style={{ width: '15%' }}>Phone</th>
                    <th style={{ width: '15%' }}>License</th>
                    <th style={{ width: '15%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransportPersons.map((person) => (
                        <tr key={person.id}>
                      <td style={{ color: '#000000' }}>
                        <strong>{person.name}</strong>
                          </td>
                      <td className="email-cell">
                        <a href={`mailto:${person.email}`} className="email-link">
                          {person.email}
                        </a>
                          </td>
                      <td style={{ color: '#000000' }}>{person.car_number}</td>
                      <td style={{ color: '#000000' }}>{person.phone || '-'}</td>
                      <td style={{ color: '#000000' }}>{person.license_number || '-'}</td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                              {viewMode === 'active' ? (
                                <>
                                 <button
                                  className="view-btn"
                                  title="View Details"
                                  onClick={() => handleViewDriver(person)}
                                  style={{
                                    background: 'transparent',
                                    color: '#FF8C42',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    marginRight: '0.25rem'
                                  }}
                                >
                                  👁️
                                </button>
                                <button 
                                  className="edit-btn"
                                  title="Edit Driver"
                                  onClick={() => handleEditDriver(person)}
                                  style={{
                                    background: 'transparent',
                                    color: '#FF8C42',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    marginRight: '0.25rem'
                                  }}
                                >
                                  ✏️
                                </button>
                                <button 
                                  className="archive-btn"
                                  title="Archive Driver"
                                  onClick={() => handleArchiveTransportPerson(person)}
                                  disabled={isSubmitting}
                                  style={{
                                    background: 'transparent',
                                    color: '#8B5CF6',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    marginRight: '0.25rem',
                                    opacity: isSubmitting ? 0.5 : 1
                                  }}
                                >
                                  📦
                                 </button>
                                 <button
                                  className="delete-btn"
                                  title="Delete Driver"
                                   onClick={() => handleDeleteTransportPerson(person.id, person.name)}
                                   disabled={isSubmitting}
                                  style={{
                                    background: 'transparent',
                                    color: '#FF8C42',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    opacity: isSubmitting ? 0.5 : 1
                                  }}
                                >
                                  🗑️
                                 </button>
                                </>
                              ) : (
                                <>
                             <button
                                  className="unarchive-btn"
                                  title="Restore Driver"
                                  onClick={() => handleRestoreTransportPerson(person)}
                                  disabled={isSubmitting}
                                  style={{
                                    background: 'transparent',
                                    color: '#10B981',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    marginRight: '0.25rem',
                                    opacity: isSubmitting ? 0.5 : 1
                                  }}
                                >
                                  ♻️
                             </button>
                             <button
                                  className="delete-btn"
                                  title="Delete Driver"
                                onClick={() => handleDeleteTransportPerson(person.id, person.name)}
                                    disabled={isSubmitting}
                                  style={{
                                    background: 'transparent',
                                    color: '#FF8C42',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    opacity: isSubmitting ? 0.5 : 1
                                  }}
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                        </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

      {/* View Driver Details Popup */}
      {viewingDriver && (
        <div className="popup-overlay" onClick={closeViewPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '2rem' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Driver Details: {viewingDriver.name}
              </h3>
              <button className="close-btn" onClick={closeViewPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
        </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              {/* Profile Picture */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', position: 'relative', minHeight: '200px' }}>
                {viewingDriver.profile_picture_url ? (
                  <>
                    <img 
                      src={viewingDriverProfileUrl || viewingDriver.profile_picture_url} 
                      alt={viewingDriver.name}
                      key={`driver-img-${viewingDriver.id}-${Date.now()}`}
                      style={{
                        width: '200px',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        border: '4px solid #FF8C42',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        display: 'block',
                        position: 'relative',
                        zIndex: 2,
                        backgroundColor: '#FFFFFF'
                      }}
                      onLoad={(e) => {
                        console.log('Profile picture loaded successfully');
                        // Hide placeholder when image loads
                        const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                        if (placeholder) placeholder.style.display = 'none';
                        e.target.style.display = 'block';
                      }}
                      onError={async (e) => {
                        console.log('Image load error, attempting to regenerate URL. Current src:', e.target.src);
                        console.log('Original URL:', viewingDriver.profile_picture_url);
                        
                        if (viewingDriver.profile_picture_url) {
                          try {
                            const newUrl = await getValidFileUrl(viewingDriver.profile_picture_url);
                            console.log('Generated new URL:', newUrl);
                            
                            if (newUrl && newUrl !== e.target.src) {
                              // Try the new URL
                              e.target.src = newUrl;
                              setViewingDriverProfileUrl(newUrl);
                            } else if (e.target.src === viewingDriver.profile_picture_url && viewingDriver.profile_picture_url.includes('supabase.co')) {
                              // If original URL failed and it's a Supabase URL, try extracting path and generating signed URL
                              const urlMatch = viewingDriver.profile_picture_url.match(/\/storage\/v1\/object\/([^/]+)\/(.+)/);
                              if (urlMatch) {
                                const bucket = urlMatch[1];
                                const filePath = decodeURIComponent(urlMatch[2]);
                                const { data, error } = await supabase.storage
                                  .from(bucket)
                                  .createSignedUrl(filePath, 3600);
                                
                                if (!error && data?.signedUrl) {
                                  e.target.src = data.signedUrl;
                                  setViewingDriverProfileUrl(data.signedUrl);
                                } else {
                                  // Show placeholder
                                  e.target.style.display = 'none';
                                  const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                                  if (placeholder) placeholder.style.display = 'flex';
                                }
                              } else {
                                // Show placeholder
                                e.target.style.display = 'none';
                                const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                                if (placeholder) placeholder.style.display = 'flex';
                              }
                            } else {
                              // Show placeholder
                              e.target.style.display = 'none';
                              const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                              if (placeholder) placeholder.style.display = 'flex';
                            }
                          } catch (err) {
                            console.error('Error regenerating URL:', err);
                            e.target.style.display = 'none';
                            const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                            if (placeholder) placeholder.style.display = 'flex';
                          }
                        } else {
                          e.target.style.display = 'none';
                          const placeholder = document.getElementById(`driver-placeholder-${viewingDriver.id}`);
                          if (placeholder) placeholder.style.display = 'flex';
                        }
                      }}
                    />
                    <div 
                      id={`driver-placeholder-${viewingDriver.id}`}
                      style={{
                        display: 'none',
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        border: '4px solid #FF8C42',
                        backgroundColor: '#E5E7EB',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '4rem',
                        color: '#9CA3AF',
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1
                      }}
                    >
                      🚌
      </div>
                  </>
                ) : (
                  <div 
                    style={{
                      display: 'flex',
                      width: '200px',
                      height: '200px',
                      borderRadius: '50%',
                      border: '4px solid #FF8C42',
                      backgroundColor: '#E5E7EB',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '4rem',
                      color: '#9CA3AF'
                    }}
                  >
                    🚌
                  </div>
                )}
              </div>

              {/* Personal Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem', textAlign: 'center' }}>
                  Personal Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>First Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingDriver.first_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Middle Name</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.middle_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Last Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingDriver.last_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Full Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingDriver.name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Employee ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.employee_id || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.email ? (
                          <a href={`mailto:${viewingDriver.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingDriver.email}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.phone ? (
                          <a href={`tel:${viewingDriver.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingDriver.phone}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          backgroundColor: viewingDriver.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingDriver.is_active ? '#065F46' : '#991B1B'
                        }}>
                          {viewingDriver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Created Date</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.created_at ? new Date(viewingDriver.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem', textAlign: 'center' }}>
                  Driver Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Car Number</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.car_number || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>License Number</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.license_number || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem', textAlign: 'center' }}>
                  Address Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.address || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Country</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.country || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>City</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.city || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Postal Code</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.postal_code || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>National/Government ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingDriver.national_id || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Driver Popup */}
      {editingDriver && (
        <div className="popup-overlay" onClick={closeEditPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Edit Driver: {editingDriver.name}
              </h3>
              <button className="close-btn" onClick={closeEditPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} style={{ padding: '1.5rem' }}>
              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="form-row three-columns">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Middle Name</label>
                    <input
                      type="text"
                      name="middle_name"
                      value={editForm.middle_name || ''}
                      onChange={(e) => setEditForm({...editForm, middle_name: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      Profile Picture
                    </label>
                    <div style={{
                      position: 'relative',
                      marginTop: '0.5rem',
                      width: '100%'
                    }}>
                      <input
                        type="file"
                        id="editProfilePicture"
                        name="profile_picture"
                        accept="image/*"
                        onChange={handleEditProfilePictureChange}
                        style={{
                          position: 'absolute',
                          width: '0.1px',
                          height: '0.1px',
                          opacity: 0,
                          overflow: 'hidden',
                          zIndex: -1,
                          pointerEvents: 'none'
                        }}
                      />
                      <label
                        htmlFor="editProfilePicture"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.4rem 0.6rem',
                          background: '#FFFFFF',
                          color: '#374151',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '400',
                          transition: 'all 0.2s ease',
                          width: '100%',
                          textAlign: 'center',
                          gap: '0.5rem',
                          userSelect: 'none',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#F9FAFB';
                          e.currentTarget.style.borderColor = '#9CA3AF';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#FFFFFF';
                          e.currentTarget.style.borderColor = '#D1D5DB';
                        }}
                      >
                        <span style={{ fontSize: '0.9rem' }}>📷</span>
                        <span>{editForm.profile_picture ? 'Change Picture' : 'Choose Picture'}</span>
                      </label>
                    </div>
                    {(editForm.profile_picture || editProfilePicturePreview) && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '3px solid #FF8C42',
                          flexShrink: 0,
                          position: 'relative'
                        }}>
                          {editProfilePicturePreview ? (
                            <img 
                              src={editProfilePicturePreview} 
                              alt="Profile preview" 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={async (e) => {
                                if (editingDriver.profile_picture_url) {
                                  try {
                                    const newUrl = await getValidFileUrl(editingDriver.profile_picture_url);
                                    if (newUrl) {
                                      e.target.src = newUrl;
                                    } else {
                                      e.target.style.display = 'none';
                                    }
                                  } catch (err) {
                                    e.target.style.display = 'none';
                                  }
                                }
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              background: '#E5E7EB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '2rem'
                            }}>
                              👤
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          {editForm.profile_picture && (
                            <>
                              <div style={{ 
                                fontWeight: '600', 
                                color: '#1F2937', 
                                marginBottom: '0.25rem', 
                                wordBreak: 'break-word',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {editForm.profile_picture.name}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                {(editForm.profile_picture.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            </>
                          )}
                        </div>
                        {(editForm.profile_picture || editProfilePicturePreview) && (
                          <button
                            type="button"
                            onClick={removeEditProfilePicture}
                            style={{
                              padding: '0.4rem 0.6rem',
                              background: '#EF4444',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                              width: 'auto',
                              height: 'auto',
                              lineHeight: '1.2'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#DC2626';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#EF4444';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    <small style={{ 
                      display: 'block', 
                      marginTop: '0.5rem', 
                      color: '#6B7280', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic'
                    }}>
                      Max size: 5MB. Accepted formats: JPG, PNG, GIF
                    </small>
                  </div>
                  
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label htmlFor="editNationalId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      National/Government ID
                    </label>
                    <input
                      type="text"
                      id="editNationalId"
                      name="national_id"
                      value={editForm.national_id || ''}
                      onChange={(e) => setEditForm({...editForm, national_id: e.target.value})}
                      placeholder="Enter National/Government ID (optional)"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Driver Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Car Number *</label>
                    <input
                      type="text"
                      name="car_number"
                      value={editForm.car_number}
                      onChange={(e) => setEditForm({...editForm, car_number: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>License Number</label>
                    <input
                      type="text"
                      name="license_number"
                      value={editForm.license_number || ''}
                      onChange={(e) => setEditForm({...editForm, license_number: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Address Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-row three-columns">
                  <div className="form-group">
                    <label>Country *</label>
                    <input
                      type="text"
                      name="country"
                      value={editForm.country}
                      onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={editForm.city}
                      onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Postal Code *</label>
                    <input
                      type="text"
                      name="postal_code"
                      value={editForm.postal_code}
                      onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>National/Government ID</label>
                    <input
                      type="text"
                      name="national_id"
                      value={editForm.national_id || ''}
                      onChange={(e) => setEditForm({...editForm, national_id: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Status</h3>
                <div className="form-row">
                  <div className="form-group" style={{ width: '100%' }}>
                    <div
                      onClick={() => setEditForm({...editForm, is_active: !editForm.is_active})}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem 1.5rem',
                        cursor: 'pointer',
                        borderRadius: '10px',
                        background: editForm.is_active ? '#D1FAE5' : '#FEE2E2',
                        border: editForm.is_active ? '2px solid #10B981' : '2px solid #EF4444',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (editForm.is_active) {
                          e.currentTarget.style.background = '#A7F3D0';
                          e.currentTarget.style.borderColor = '#059669';
                        } else {
                          e.currentTarget.style.background = '#FECACA';
                          e.currentTarget.style.borderColor = '#DC2626';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (editForm.is_active) {
                          e.currentTarget.style.background = '#D1FAE5';
                          e.currentTarget.style.borderColor = '#10B981';
                        } else {
                          e.currentTarget.style.background = '#FEE2E2';
                          e.currentTarget.style.borderColor = '#EF4444';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          background: editForm.is_active ? '#10B981' : '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          {editForm.is_active ? (
                            <span style={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 'bold' }}>✓</span>
                          ) : (
                            <span style={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 'bold' }}>✕</span>
                          )}
                        </div>
                        <span style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: editForm.is_active ? '#065F46' : '#991B1B'
                        }}>
                          {editForm.is_active ? 'Active Driver' : 'Inactive Driver'}
                        </span>
                      </div>
                      <div style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        background: editForm.is_active ? '#10B981' : '#EF4444',
                        color: '#FFFFFF',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}>
                        {editForm.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeEditPopup}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting || uploadingEditPicture}>
                  {uploadingEditPicture ? 'Uploading Picture...' : isSubmitting ? 'Updating...' : 'Update Driver'}
                </button>
              </div>
            </form>
        </div>
      </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && driverToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveDriver}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>📦</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Archive Driver?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to archive <strong>{driverToArchive.name || `${driverToArchive.first_name} ${driverToArchive.last_name}`}</strong>?
              <br /><br />
              The driver will be marked as inactive and hidden from the active drivers list. They can be restored later if needed.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelArchiveDriver} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmArchiveDriver} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)' }}>
                {isSubmitting ? 'Archiving...' : 'Archive Driver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Driver Archived Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{archivedDriverName}</strong> has been archived successfully.
              <br />
              They are now marked as inactive.
            </p>
            <button onClick={() => setShowArchiveSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && driverToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreDriver}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>♻️</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Restore Driver?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to restore <strong>{driverToRestore.name || `${driverToRestore.first_name} ${driverToRestore.last_name}`}</strong>?
              <br /><br />
              The driver will be marked as active and will appear in the active drivers list.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelRestoreDriver} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmRestoreDriver} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
                {isSubmitting ? 'Restoring...' : 'Restore Driver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Driver Restored Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{restoredDriverName}</strong> has been restored successfully.
              <br />
              The driver is now marked as active.
            </p>
            <button onClick={() => setShowRestoreSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && transportPersonToDelete && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelDeleteTransportPerson}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Delete Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>🗑️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Delete Transport Person?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to <strong style={{ color: '#EF4444' }}>PERMANENTLY DELETE</strong> <strong>{transportPersonToDelete.name}</strong>?
              <br /><br />
              This will:
              <br />
              • Remove the transport person from the database completely
              <br />
              • Delete their login account (if exists)
              <br />
              <strong style={{ color: '#EF4444' }}>This action CANNOT be undone!</strong>
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelDeleteTransportPerson}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FFFFFF';
                  e.target.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTransportPerson}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowDeleteSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Transport Person Deleted Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{deletedTransportPersonName}</strong> has been permanently deleted from the database.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowDeleteSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TransportPersons;
